import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Phone, Mail, Star, Languages } from 'lucide-react';
import PropertyCard from '../components/PropertyCard';
import { Button } from '../components/ui/button';
import api, { enrichAgent, enrichProperty } from '../lib/api';

export default function AgentDetail() {
  const { id } = useParams();
  const [agent, setAgent] = useState(null);
  const [listings, setListings] = useState([]);

  useEffect(() => {
    api.get('/agents/' + id).then(r => {
      const a = enrichAgent(r.data);
      setAgent(a);
      const ls = (r.data?.active_listings || []).map(enrichProperty);
      setListings(ls);
    });
  }, [id]);

  if (!agent) return <div className="min-h-screen flex items-center justify-center text-neutral-500">Loading advisor...</div>;

  return (
    <div className="bg-neutral-50 min-h-screen">
      <div className="bg-gradient-to-r from-neutral-900 to-neutral-800 text-white">
        <div className="max-w-7xl mx-auto px-6 py-12 flex flex-col md:flex-row gap-8 items-start">
          <img src={agent.photo} alt={agent.name} className="w-40 h-40 rounded-xl object-cover border-4 border-white shadow-lg" />
          <div className="flex-1">
            <p className="text-sm uppercase tracking-wider text-[#DC2626] font-semibold">{agent.title}</p>
            <h1 className="text-3xl md:text-4xl font-bold mt-1">{agent.name}</h1>
            <p className="text-neutral-300 mt-1">{agent.license}</p>
            <p className="mt-4 text-neutral-200 max-w-2xl">{agent.bio}</p>
            <div className="mt-5 flex flex-wrap gap-4">
              <a href={'tel:' + agent.phone}><Button className="btn-primary gap-2"><Phone className="w-4 h-4" /> Call</Button></a>
              <a href={'mailto:' + agent.email}><Button variant="outline" className="gap-2 bg-transparent border-white text-white hover:bg-white hover:text-neutral-900"><Mail className="w-4 h-4" /> Email</Button></a>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1">
          <div className="bg-white border border-neutral-200 rounded-xl p-5 sticky top-24">
            <h3 className="font-bold text-neutral-900 mb-3">Performance</h3>
            <ul className="space-y-3 text-sm">
              <li className="flex justify-between"><span className="text-neutral-500">Rating</span><span className="font-semibold flex items-center gap-1"><Star className="w-4 h-4 text-[#DC2626] fill-[#DC2626]" /> {agent.rating}/5</span></li>
              <li className="flex justify-between"><span className="text-neutral-500">Sold</span><span className="font-semibold">{agent.sold}</span></li>
              <li className="flex justify-between"><span className="text-neutral-500">Active Listings</span><span className="font-semibold">{listings.length}</span></li>
            </ul>
            {agent.languages?.length > 0 && (
              <div className="mt-5 pt-5 border-t border-neutral-100">
                <p className="text-xs font-semibold text-neutral-500 uppercase mb-2 flex items-center gap-1"><Languages className="w-3 h-3" /> Languages</p>
                <div className="flex flex-wrap gap-2">{agent.languages.map((l) => <span key={l} className="px-2 py-1 bg-neutral-100 text-xs rounded">{l}</span>)}</div>
              </div>
            )}
            <div className="mt-5 pt-5 border-t border-neutral-100">
              <p className="text-xs font-semibold text-neutral-500 uppercase mb-2">Contact</p>
              <a href={'tel:' + agent.phone} className="flex items-center gap-2 text-sm text-neutral-700 hover:text-[#DC2626] py-1"><Phone className="w-4 h-4" /> {agent.phone}</a>
              <a href={'mailto:' + agent.email} className="flex items-center gap-2 text-sm text-neutral-700 hover:text-[#DC2626] py-1"><Mail className="w-4 h-4" /> {agent.email}</a>
            </div>
          </div>
        </div>
        <div className="lg:col-span-3">
          <h2 className="text-2xl font-bold text-neutral-900 mb-5">Active Listings ({listings.length})</h2>
          {listings.length === 0 ? (
            <div className="bg-white p-12 text-center rounded-xl border border-neutral-200">
              <p className="text-neutral-600">This advisor has no active listings.</p>
              <Link to="/properties"><Button className="btn-primary mt-4">Browse All Properties</Button></Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {listings.map((p) => <PropertyCard key={p.id} property={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
